import { apiClient } from '@shared/api/api-client';
import { logger } from '@shared/lib/logger';
import { queryPersister } from '@shared/lib/react-query-persist';
import { queryClient } from '@shared/lib/react-query';

export const ACTIVE_USER_STORAGE_KEY = 'jema_active_user_id_v2';

const GLOBAL_STORAGE_KEYS_TO_PRESERVE = new Set([
  'theme',
  'jema-manager-locale',
  'jema_supabase_auth',
  'jema_supabase_auth-code-verifier',
]);

const GLOBAL_STORAGE_PREFIXES_TO_PRESERVE = ['sb-', 'supabase.', 'jema_supabase_auth'];

const USER_SCOPED_STORAGE_PREFIXES = [
  'jema_',
  'jema:',
  'doctor-quote-workspace:',
  'kanban-column-collapsed-',
];

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const safeGetItem = (key: string) => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    logger.warn('Failed to read localStorage key during session isolation', { key, error });
    return null;
  }
};

const safeSetItem = (key: string, value: string) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    logger.warn('Failed to write localStorage key during session isolation', { key, error });
  }
};

const safeRemoveItem = (key: string) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    logger.warn('Failed to remove localStorage key during session isolation', { key, error });
  }
};

const getAllLocalStorageKeys = () => {
  if (!isBrowser()) return [];

  try {
    const keys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i);
      if (key) keys.push(key);
    }
    return keys;
  } catch (error) {
    logger.warn('Failed to enumerate localStorage keys during session isolation', { error });
    return [];
  }
};

const isPreservedGlobalKey = (key: string) => {
  if (GLOBAL_STORAGE_KEYS_TO_PRESERVE.has(key)) return true;
  if (key === ACTIVE_USER_STORAGE_KEY) return true;
  return GLOBAL_STORAGE_PREFIXES_TO_PRESERVE.some((prefix) => key.startsWith(prefix));
};

const isUserScopedStorageKey = (key: string) => {
  if (isPreservedGlobalKey(key)) return false;
  if (key === 'userId') return true;
  return USER_SCOPED_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix));
};

const clearUserScopedLocalStorage = () => {
  if (!isBrowser()) return 0;

  let removedCount = 0;
  const keys = getAllLocalStorageKeys();
  for (const key of keys) {
    if (!isUserScopedStorageKey(key)) continue;
    safeRemoveItem(key);
    removedCount += 1;
  }

  return removedCount;
};

export const clearUserScopedClientState = async (reason: string) => {
  if (!isBrowser()) return;

  let removedStorageKeys = 0;

  try {
    queryClient.clear();
  } catch (error) {
    logger.warn('Failed to clear React Query cache during session isolation', { reason, error });
  }

  try {
    apiClient.clearSessionBoundState(reason);
  } catch (error) {
    logger.warn('Failed to clear API client runtime state during session isolation', {
      reason,
      error,
    });
  }

  try {
    await queryPersister.removeClient();
  } catch (error) {
    logger.warn('Failed to remove persisted React Query cache during session isolation', {
      reason,
      error,
    });
  }

  try {
    removedStorageKeys = clearUserScopedLocalStorage();
  } catch (error) {
    logger.warn('Failed to clear user-scoped localStorage during session isolation', {
      reason,
      error,
    });
  }

  logger.info('Cleared user-scoped client state', {
    reason,
    removedStorageKeys,
  });
};

export const synchronizeClientStateWithUser = async (nextUserId: string | null) => {
  if (!isBrowser()) return;

  try {
    const activeUserId = safeGetItem(ACTIVE_USER_STORAGE_KEY);
    const switchedUsers = Boolean(activeUserId && nextUserId && activeUserId !== nextUserId);
    const signedOut = Boolean(activeUserId && !nextUserId);
    const unownedUserScopedState = Boolean(
      nextUserId && !activeUserId && getAllLocalStorageKeys().some(isUserScopedStorageKey),
    );

    if (switchedUsers || signedOut || unownedUserScopedState) {
      const reason = switchedUsers
        ? 'user_switched'
        : signedOut
          ? 'signed_out'
          : 'unowned_user_scoped_state';
      await clearUserScopedClientState(reason);
    }

    if (nextUserId) {
      safeSetItem(ACTIVE_USER_STORAGE_KEY, nextUserId);
    } else {
      safeRemoveItem(ACTIVE_USER_STORAGE_KEY);
    }
  } catch (error) {
    logger.warn('Session isolation synchronization failed; continuing auth flow', {
      nextUserId,
      error,
    });
  }
};
